import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useMemo } from 'react';
import { Icon16Check } from '@figma/fpl-icons/icon-16-check';
import equal from 'fast-deep-equal';
import { useIdProvider } from '../hooks/id-provider.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { accept } from '../utils/event.js';
import { isStandardShortcutKeyPressed } from '../utils/modifiers.js';
import { Group } from './menu-v2.group.js';
import { MenuInternalContext, useMenuItemSelection, useMenuItemSurfaceProps } from './menu-v2.hooks.js';
import { ItemDisplay } from './menu-v2.item.js';
import { item } from './menu-v2.css.js';

const GroupContext = createContext(null);
/** @see http://fpl.figma.design/primitives/menu/ **/
function _RadioGroup({ onChange, disabled = false, children, value, recordingKey, ...props }, ref) {
    const [id, IdProvider] = useIdProvider();
    const recordedAction = useInteractionHandler(onChange, { eventName: 'change', recordingKey }, [
        onChange,
    ]);
    const groupContext = useMemo(() => {
        return {
            onChange(newValue, event) {
                recordedAction(newValue, { event });
            },
            currentValue: value,
            disabledGroup: disabled,
        };
    }, [disabled, value, recordedAction]);
    return (jsx(IdProvider, { value: id, children: jsx(GroupContext.Provider, { value: groupContext, children: jsx(Group, { ref: ref, ...props, children: children }) }) }));
}
_RadioGroup.displayName = 'MenuV2.RadioGroup';
/**
 * Grouping of Menu.RadioGroupItems that are part of one radio group.
 *
 * @compose { "parent": ["MenuV2.Root", "MenuV2.SubMenu"], "multi": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/menu-v2
 */
const RadioGroup = forwardRef(_RadioGroup);
/** @see https://fpl.figma.design/components/menu-v2 */
function _RadioGroupItem({ children, value, disabled, lead, targetId, trail, ...props }, refIn) {
    const { onCloseRoot, isClickHold } = useValidatedContext(MenuInternalContext, 'MenuV2.RadioGroupItem', 'Menu.Root');
    const { currentValue, onChange, disabledGroup } = useValidatedContext(GroupContext, 'MenuV2.RadioGroupItem', 'MenuV2.RadioGroup');
    const isDisabled = disabledGroup || disabled;
    const isSelected = equal(value, currentValue);
    const { itemProps, targetRef } = useMenuItemSelection({ targetId });
    const ref = useMergeRefs(refIn, targetRef);
    const { interactionProps, paintProps } = useMenuItemSurfaceProps({
        targetId,
        disabled: isDisabled,
        treatment: 'brand',
    });
    const handleClick = (e) => {
        if (!isDisabled) {
            onChange(value, e);
            if (!isStandardShortcutKeyPressed(e) || isClickHold.current) {
                onCloseRoot();
            }
        }
        accept(e);
    };
    const handlePointerUp = (e) => {
        if (isClickHold.current)
            handleClick(e);
    };
    const handleKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            if (!isDisabled) {
                onChange(value, e);
                onCloseRoot();
            }
            accept(e); // accept event regardless of disabled
        }
    };
    return (jsx("li", { ...interactionProps, ...props, ...itemProps, className: item, role: "menuitemradio", ref: ref, "aria-checked": isSelected, "aria-disabled": isDisabled || undefined, onClick: handleClick, onPointerUp: handlePointerUp, onKeyDown: handleKeyDown, children: jsx(ItemDisplay, { ...paintProps, selectIcon: jsx(Icon16Check, { "aria-hidden": true }), lead: lead, trail: trail, variant: "default", children: children }) }));
}
_RadioGroupItem.displayName = 'MenuV2.RadioGroupItem';
/**
 * Member of Menu.RadioGroup.
 *
 * @compose { "parent": ["MenuV2.RadioGroup"], "multi": true }
 * @see https://fpl.figma.design/components/menu-v2
 */
const RadioGroupItem = forwardRef(_RadioGroupItem);

export { RadioGroup, RadioGroupItem };
//# sourceMappingURL=menu-v2.radio.js.map
